import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Building2, MapPin, ExternalLink, Sun, Battery, Zap, 
  Plus, Edit, Trash2, Wrench, Sparkles, FileText, Upload,
  Calendar as CalendarIcon, User, Mail, Eye, Download, HardDrive, Loader2, Phone, CheckCircle2, AlertTriangle, ChevronDown, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Calendar } from '../components/ui/calendar';
import { format, parse } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api, { propertyAPI, installationAPI, maintenanceAPI, cleaningAPI, documentAPI, offerteAPI } from '../lib/api';
import { formatDate, getGoogleMapsUrl, getWazeUrl, getInstallationTypeBadgeClass, getInstallationTypeLabel } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import InstallationForm from '../components/property/InstallationForm';

// Google Sheets gid values for installation quotes/invoices
const INSTALLATION_SHEET_GIDS = {
  offerte: {
    SAJ: '542498245',
    Solarwatt: '2141029867',
    DMEGC: '1654839085'
  },
  factuur: {
    SAJ: '2126887706',
    Solarwatt: '357251815',
    DMEGC: '1042547387'
  }
};

const GOOGLE_SHEET_BASE_URL = 'https://docs.google.com/spreadsheets/d/1Jp2j7rcPWuBbOTLCKtTaYlxvgJ-P0I-4Ww-NOxoLkW0/edit#gid=';

const PropertyDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [pushingDocId, setPushingDocId] = useState(null);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [bulkPushing, setBulkPushing] = useState(false);

  const toggleDocSelection = (docId) => {
    setSelectedDocIds(prev => prev.includes(docId) ? prev.filter(i => i !== docId) : [...prev, docId]);
  };

  const handleBulkPushToSharepoint = async () => {
    if (selectedDocIds.length === 0) return;
    setBulkPushing(true);
    let ok = 0, failed = 0;
    for (const docId of selectedDocIds) {
      try {
        await api.post(`/documents/${docId}/push-to-sharepoint`);
        ok++;
      } catch (err) {
        failed++;
      }
    }
    setBulkPushing(false);
    setSelectedDocIds([]);
    if (failed === 0) {
      toast.success(`${ok} foto('s) naar de klantmap gepusht`);
    } else {
      toast.warning(`${ok} gelukt, ${failed} mislukt`);
    }
  };

  const handlePushToSharepoint = async (doc) => {
    setPushingDocId(doc.id);
    try {
      const res = await api.post(`/documents/${doc.id}/push-to-sharepoint`);
      toast.success(res.data.message || 'Naar klantmap gepusht');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kon niet naar klantmap pushen');
    } finally {
      setPushingDocId(null);
    }
  };
  const navigate = useNavigate();
  const { hasRole, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [installationDialog, setInstallationDialog] = useState(false);
  const [maintenanceDialog, setMaintenanceDialog] = useState(false);
  const [cleaningDialog, setCleaningDialog] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form states
  const [maintenanceForm, setMaintenanceForm] = useState({
    maintenance_date: '',
    maintenance_type: '',
    technician: '',
    notes: ''
  });
  const [cleaningForm, setCleaningForm] = useState({
    cleaning_date: '',
    performed_by: '',
    notes: ''
  });
  const [uploadForm, setUploadForm] = useState({
    document_type: 'invoice',
    files: [],
    description: ''
  });
  
  // State for document delete
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteDocumentDialogOpen, setDeleteDocumentDialogOpen] = useState(false);
  
  // State for document preview
  const [previewDocument, setPreviewDocument] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  
  // State for installation type selection modal
  const [installatieModalOpen, setInstallatieModalOpen] = useState(false);
  const [installatieModalType, setInstallatieModalType] = useState(null); // 'offerte' or 'factuur'
  
  // State for Offerte Reiniging modal
  const [offerteReinigingModalOpen, setOfferteReinigingModalOpen] = useState(false);
  const [offerteReinigingForm, setOfferteReinigingForm] = useState({
    inplannen: false,
    inplanDatum: '',
    inplanTijd: '',
    bevestigingsmail: false
  });
  
  // State for email confirmation after offerte creation
  const [emailConfirmModalOpen, setEmailConfirmModalOpen] = useState(false);
  const [createdOfferteId, setCreatedOfferteId] = useState(null);

  // State for editing property (address)
  const [editPropertyDialogOpen, setEditPropertyDialogOpen] = useState(false);
  const [editPropertyForm, setEditPropertyForm] = useState(null);
  const [savingProperty, setSavingProperty] = useState(false);

  // State for editing installation
  const [editInstallation, setEditInstallation] = useState(null);
  const [editInstallationDialogOpen, setEditInstallationDialogOpen] = useState(false);

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [installationToDelete, setInstallationToDelete] = useState(null);

  // Elektriciteit offerte state
  const [elektriciteitDialogOpen, setElektriciteitDialogOpen] = useState(false);
  const [elektriciteitFile, setElektriciteitFile] = useState(null);
  const [elektriciteitUploading, setElektriciteitUploading] = useState(false);
  const elektriciteitInputRef = useRef(null);

  const fetchData = async () => {
    try {
      const response = await propertyAPI.getDetails(id);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load property details');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleCreateInstallation = async (formData) => {
    setSaving(true);
    try {
      await installationAPI.create(formData);
      toast.success('Installation added successfully');
      setInstallationDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add installation');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteInstallation = async (instId) => {
    setInstallationToDelete(instId);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteInstallation = async () => {
    if (!installationToDelete) return;
    
    try {
      await installationAPI.delete(installationToDelete);
      toast.success('Installatie verwijderd');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Kon installatie niet verwijderen');
    } finally {
      setDeleteConfirmOpen(false);
      setInstallationToDelete(null);
    }
  };

  // Edit property (address) handlers
  const handleEditProperty = () => {
    if (!property) return;
    setEditPropertyForm({
      street: property.street || '',
      number: property.number || '',
      postal_code: property.postal_code || '',
      city: property.city || '',
      country: property.country || 'Belgium',
      notes: property.notes || ''
    });
    setEditPropertyDialogOpen(true);
  };

  const handleSaveProperty = async () => {
    if (!editPropertyForm) return;
    setSavingProperty(true);
    try {
      await propertyAPI.update(id, editPropertyForm);
      toast.success('Adres bijgewerkt');
      setEditPropertyDialogOpen(false);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Kon adres niet bijwerken';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message).join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setSavingProperty(false);
    }
  };

  // Edit installation handlers
  const handleEditInstallation = (installation) => {
    setEditInstallation(installation);
    setEditInstallationDialogOpen(true);
  };

  const handleSaveInstallation = async (formData) => {
    setSaving(true);
    try {
      if (editInstallation) {
        await installationAPI.update(editInstallation.id, formData);
        toast.success('Installatie bijgewerkt');
      } else {
        await installationAPI.create(formData);
        toast.success('Installatie toegevoegd');
      }
      setEditInstallationDialogOpen(false);
      setEditInstallation(null);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Kon installatie niet opslaan';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message).join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateMaintenance = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await maintenanceAPI.create({ ...maintenanceForm, property_id: id });
      toast.success('Maintenance record added');
      setMaintenanceDialog(false);
      setMaintenanceForm({ maintenance_date: '', maintenance_type: '', technician: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add maintenance');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCleaning = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await cleaningAPI.create({ ...cleaningForm, property_id: id });
      toast.success('Cleaning record added');
      setCleaningDialog(false);
      setCleaningForm({ cleaning_date: '', performed_by: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add cleaning');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!uploadForm.files || uploadForm.files.length === 0) return;
    
    setSaving(true);
    const results = { ok: 0, failed: [] };
    try {
      for (const file of uploadForm.files) {
        try {
          const formData = new FormData();
          formData.append('entity_type', 'property');
          formData.append('entity_id', id);
          formData.append('document_type', uploadForm.document_type);
          formData.append('file', file);
          if (uploadForm.description) {
            formData.append('description', uploadForm.description);
          }
          await documentAPI.upload(formData);
          results.ok += 1;
        } catch (fileErr) {
          console.error(`Upload failed for ${file.name}:`, fileErr);
          results.failed.push(file.name);
        }
      }
      if (results.ok > 0 && results.failed.length === 0) {
        toast.success(`${results.ok} document${results.ok > 1 ? 'en' : ''} geüpload`);
      } else if (results.ok > 0 && results.failed.length > 0) {
        toast.warning(`${results.ok} geüpload, ${results.failed.length} mislukt: ${results.failed.join(', ')}`);
      } else {
        toast.error('Kon documenten niet uploaden');
      }
      setUploadDialog(false);
      setUploadForm({ document_type: 'invoice', files: [], description: '' });
      fetchData();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;
    
    try {
      await documentAPI.delete(documentToDelete.id);
      toast.success('Document verwijderd');
      setDeleteDocumentDialogOpen(false);
      setDocumentToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Kon document niet verwijderen');
    }
  };

  // Get file type for preview
  const getFileType = (filename) => {
    if (!filename) return 'other';
    const ext = filename.toLowerCase().split('.').pop();
    if (['pdf'].includes(ext)) return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
    return 'other';
  };

  // Get preview URL - use local files when available (no auth issues)
  const getPreviewUrl = (doc) => {
    if (!doc) return '';
    
    // local_path kan een volledige URL zijn (R2) of een relatief pad (/api/uploads/...)
    if (doc.local_path) {
      return doc.local_path.startsWith('http')
        ? doc.local_path
        : `${process.env.REACT_APP_BACKEND_URL}${doc.local_path}`;
    }
    
    // Fallback to file_path
    if (doc.file_path) {
      if (doc.file_path.startsWith('http')) return doc.file_path;
      // file_path may be stored as /uploads/... and needs /api/uploads/...
      const apiPath = doc.file_path.startsWith('/api/') 
        ? doc.file_path 
        : doc.file_path.replace('/uploads/', '/api/uploads/');
      return `${process.env.REACT_APP_BACKEND_URL}${apiPath}`;
    }
    
    const fileType = getFileType(doc.filename);
    
    // For PDFs without local file, try Google Drive preview
    if (fileType === 'pdf' && doc.google_drive_file_id) {
      return `https://drive.google.com/file/d/${doc.google_drive_file_id}/preview`;
    }
    
    // For images without local file, try direct download URL
    if (fileType === 'image' && doc.google_drive_download_url) {
      return doc.google_drive_download_url;
    }
    
    // Last resort - Google Drive URL
    return doc.google_drive_url || '';
  };

  // Get download URL for the document
  const getDownloadUrl = (doc) => {
    if (!doc) return '';
    if (doc.google_drive_download_url) {
      return doc.google_drive_download_url;
    }
    // Prefer local_path
    if (doc.local_path) {
      return doc.local_path.startsWith('http')
        ? doc.local_path
        : `${process.env.REACT_APP_BACKEND_URL}${doc.local_path}`;
    }
    if (doc.file_path) {
      if (doc.file_path.startsWith('http')) return doc.file_path;
      const apiPath = doc.file_path.startsWith('/api/') 
        ? doc.file_path 
        : doc.file_path.replace('/uploads/', '/api/uploads/');
      return `${process.env.REACT_APP_BACKEND_URL}${apiPath}`;
    }
    return doc.google_drive_url || '';
  };

  // Handle document preview click
  const handlePreviewDocument = (doc) => {
    setPreviewDocument(doc);
    setPreviewDialogOpen(true);
  };

  // Handle status checkbox change
  const handleStatusChange = async (statusField, value) => {
    // Optimistic update - update UI immediately
    setData(prev => ({
      ...prev,
      property: {
        ...prev.property,
        [statusField]: value
      }
    }));
    
    try {
      await propertyAPI.updateStatus(id, statusField, value);
      toast.success('Status bijgewerkt');
    } catch (error) {
      console.error('Error updating status:', error);
      // Revert on error
      setData(prev => ({
        ...prev,
        property: {
          ...prev.property,
          [statusField]: !value
        }
      }));
      toast.error('Kon status niet bijwerken');
    }
  };

  // Open Offerte Reiniging - Navigate to Offertes page with pre-filled data
  const handleOfferteReinigingClick = () => {
    // Get panel count from installations
    const solarInstall = installations.find(i => i.installation_type === 'solar_panels');
    const panelCount = solarInstall?.number_of_panels || solarInstall?.panel_count || 0;
    
    // Navigate to Offertes with query params to open pre-filled form
    const params = new URLSearchParams({
      action: 'create',
      type: 'reiniging',
      property_id: id,
      customer_id: property?.customer_id || '',
      panel_count: panelCount.toString()
    });
    navigate(`/offertes?${params.toString()}`);
  };

  // Create offerte in CRM and show email confirmation popup
  const handleCreateOfferteInCRM = async () => {
    setOfferteReinigingModalOpen(false);
    
    try {
      toast.loading('Offerte aanmaken...', { id: 'offerte-create' });
      
      // Get panel count from installations
      const solarInstall = installations.find(i => i.installation_type === 'solar_panels');
      const panelCount = solarInstall?.number_of_panels || solarInstall?.panel_count || 0;
      
      // Get company settings for default values
      const settingsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/company-settings`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const settings = await settingsRes.json();
      
      // Create offerte with pre-filled data
      const offerteData = {
        customer_id: property?.customer_id,
        property_id: id,
        title: `Offerte reiniging - ${property?.street} ${property?.number}`,
        contract_type: '3_yearly',
        contract_duration: '9 jaar',
        items: [
          {
            description: 'Reinigen PV panelen',
            quantity: panelCount || 1,
            unit_price: settings?.default_cleaning_price_per_panel || 12.50,
            total_price: (panelCount || 1) * (settings?.default_cleaning_price_per_panel || 12.50)
          },
          {
            description: 'Verplaatsingskosten forfait',
            quantity: 1,
            unit_price: settings?.default_travel_costs || 30.49,
            total_price: settings?.default_travel_costs || 30.49
          }
        ],
        vat_percentage: settings?.default_vat_percentage || 6,
        payment_terms: settings?.default_payment_terms || '',
        validity_days: settings?.default_validity_days || 14,
        status: 'draft',
        notes: ''
      };
      
      const response = await offerteAPI.create(offerteData);
      
      if (response.data && response.data.id) {
        toast.success('Offerte aangemaakt!', { id: 'offerte-create' });
        setCreatedOfferteId(response.data.id);
        // Show email confirmation popup
        setEmailConfirmModalOpen(true);
      } else {
        throw new Error('Kon offerte niet aanmaken');
      }
    } catch (error) {
      console.error('Create offerte error:', error);
      toast.error('Kon offerte niet aanmaken', { id: 'offerte-create' });
    }
  };

  // Send email via n8n webhook after offerte creation
  const handleSendOfferteEmail = async () => {
    setEmailConfirmModalOpen(false);
    
    try {
      toast.loading('Email verzenden...', { id: 'offerte-email' });
      
      const customerName = customer ? `${customer.first_name} ${customer.last_name}` : 'Onbekend';
      const customerEmail = customer?.email || '';
      const propertyAddress = `${property?.street} ${property?.number}, ${property?.postal_code} ${property?.city}`;
      
      // Get panel count
      const solarInstall = installations.find(i => i.installation_type === 'solar_panels');
      const panelCount = solarInstall?.number_of_panels || solarInstall?.panel_count || 0;
      
      // Send via backend to avoid CORS issues
      const webhookData = {
        offerte_id: createdOfferteId,
        customer_name: customerName,
        customer_email: customerEmail,
        property_address: propertyAddress,
        panel_count: panelCount
      };
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/offerte-reiniging-mail`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(webhookData)
      });
      
      if (response.ok) {
        toast.success('Email verzonden!', { id: 'offerte-email' });
      } else {
        throw new Error('Email kon niet verzonden worden');
      }
      // Navigate to Offertes page
      navigate('/offertes');
    } catch (error) {
      console.error('Send email error:', error);
      toast.error('Email kon niet verzonden worden', { id: 'offerte-email' });
      // Still navigate to Offertes page
      navigate('/offertes');
    }
  };

  // Skip email and go to Offertes page
  const handleSkipEmail = () => {
    setEmailConfirmModalOpen(false);
    navigate('/offertes');
  };

  // Submit Offerte Reiniging with options
  const handleOfferteReinigingSubmit = async () => {
    setOfferteReinigingModalOpen(false);
    
    try {
      toast.loading('Offerte aanvragen...', { id: 'offerte' });
      
      // Get customer name and email
      const customerName = customer ? `${customer.first_name} ${customer.last_name}` : 'Onbekend';
      const customerEmail = customer?.email || '';
      
      // Get logged-in user info (Cyril, Jorik, Koen, Charli, Kelly)
      const medewerkerNaam = user ? `${user.first_name} ${user.last_name}` : 'Onbekend';
      const medewerkerEmail = user?.email || '';
      
      // Build the message for the AI agent - clear instructions
      let aiMessage = `MEDEWERKER: ${medewerkerNaam} (${medewerkerEmail}) heeft deze offerte aangevraagd. `;
      
      if (offerteReinigingForm.inplannen && offerteReinigingForm.inplanDatum) {
        const datum = offerteReinigingForm.inplanDatum;
        const tijd = offerteReinigingForm.inplanTijd || '09:00';
        aiMessage += `ACTIE 1: Plan deze reiniging in de Google Calendar op ${datum} om ${tijd} uur voor klant ${customerName} op adres ${property?.street} ${property?.number}, ${property?.city}. `;
      }
      if (offerteReinigingForm.bevestigingsmail) {
        aiMessage += `ACTIE 2: Schrijf een bevestigingsmail GERICHT AAN DE KLANT (aanhef: "Beste ${customerName}"). De mail is voor de klant, NIET voor de medewerker. Verzend deze mail naar ${medewerkerEmail} (${medewerkerNaam}). De medewerker controleert de mail en stuurt deze zelf door naar de klant.`;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/offerte-reiniging-adres`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          property_id: id,
          // Extra options for AI agent
          inplannen: offerteReinigingForm.inplannen,
          inplan_datum: offerteReinigingForm.inplanDatum,
          inplan_tijd: offerteReinigingForm.inplanTijd || '09:00',
          bevestigingsmail: offerteReinigingForm.bevestigingsmail,
          gebruiker_email: user?.email,
          gebruiker_naam: user ? `${user.first_name} ${user.last_name}` : '',
          ai_instructie: aiMessage
        })
      });
      
      const responseData = await response.json();
      
      if (response.ok && responseData.success) {
        toast.success('Offerte aanvraag verzonden!', { id: 'offerte' });
        // Open Google Sheet - Offerte reiniging tab
        window.open('https://docs.google.com/spreadsheets/d/1Jp2j7rcPWuBbOTLCKtTaYlxvgJ-P0I-4Ww-NOxoLkW0/edit#gid=78034772', '_blank');
      } else {
        throw new Error(responseData.message || 'Webhook error');
      }
    } catch (error) {
      console.error('Offerte error:', error);
      toast.error('Kon offerte aanvraag niet verzenden', { id: 'offerte' });
    }
  };

  const handleFactuurReiniging = async () => {
    try {
      toast.loading('Factuur aanvragen...', { id: 'factuur' });
      
      // Get logged-in user info
      const medewerkerNaam = user ? `${user.first_name} ${user.last_name}` : 'Onbekend';
      const medewerkerEmail = user?.email || '';
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/factuur-reiniging-adres`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          property_id: id,
          gebruiker_email: medewerkerEmail,
          gebruiker_naam: medewerkerNaam,
          ai_instructie: `MEDEWERKER: ${medewerkerNaam} (${medewerkerEmail}) heeft deze factuur aangevraagd. Stuur eventuele bevestigingsmails naar ${medewerkerEmail}.`
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success('Factuur aanvraag verzonden!', { id: 'factuur' });
        // Open Google Sheet - Factuur reiniging tab
        window.open('https://docs.google.com/spreadsheets/d/1Jp2j7rcPWuBbOTLCKtTaYlxvgJ-P0I-4Ww-NOxoLkW0/edit#gid=1030317467', '_blank');
      } else {
        throw new Error(data.message || 'Webhook error');
      }
    } catch (error) {
      console.error('Factuur error:', error);
      toast.error('Kon factuur aanvraag niet verzenden', { id: 'factuur' });
    }
  };

  // Open modal to select installation type (SAJ, Solarwatt, DMEGC)
  const handleOfferteInstallatieClick = () => {
    setInstallatieModalType('offerte');
    setInstallatieModalOpen(true);
  };

  const handleFactuurInstallatieClick = () => {
    setInstallatieModalType('factuur');
    setInstallatieModalOpen(true);
  };

  // Handle the actual webhook call and redirect after selecting type
  const handleInstallatieTypeSelect = async (installatieType) => {
    const type = installatieModalType;
    setInstallatieModalOpen(false);
    
    const toastId = type === 'offerte' ? 'offerte-inst' : 'factuur-inst';
    const toastLabel = type === 'offerte' ? 'Offerte' : 'Factuur';
    const endpoint = type === 'offerte' ? 'offerte-installatie-adres' : 'factuur-installatie-adres';
    
    // Get logged-in user info
    const medewerkerNaam = user ? `${user.first_name} ${user.last_name}` : 'Onbekend';
    const medewerkerEmail = user?.email || '';
    
    try {
      toast.loading(`${toastLabel} installatie aanvragen...`, { id: toastId });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          property_id: id,
          gebruiker_email: medewerkerEmail,
          gebruiker_naam: medewerkerNaam,
          installatie_type: installatieType,
          ai_instructie: `MEDEWERKER: ${medewerkerNaam} (${medewerkerEmail}) heeft deze ${toastLabel.toLowerCase()} aangevraagd voor ${installatieType} installatie. Stuur eventuele bevestigingsmails naar ${medewerkerEmail}.`
        })
      });
      
      const responseData = await response.json();
      
      if (response.ok && responseData.success) {
        toast.success(`${toastLabel} installatie aanvraag verzonden!`, { id: toastId });
        // Open Google Sheet with the correct gid based on selected type
        const gid = INSTALLATION_SHEET_GIDS[type][installatieType];
        window.open(`${GOOGLE_SHEET_BASE_URL}${gid}`, '_blank');
      } else {
        throw new Error(responseData.message || 'Webhook error');
      }
    } catch (error) {
      console.error(`${toastLabel} installatie error:`, error);
      toast.error(`Kon ${toastLabel.toLowerCase()} aanvraag niet verzenden`, { id: toastId });
    }
  };

  // Handle Elektriciteit offerte
  const handleOfferteElektriciteitClick = () => {
    setElektriciteitFile(null);
    setElektriciteitDialogOpen(true);
  };

  const handleElektriciteitFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Alleen PDF bestanden zijn toegestaan');
        return;
      }
      setElektriciteitFile(file);
    }
  };

  const handleElektriciteitSubmit = async () => {
    if (!elektriciteitFile) {
      toast.error('Selecteer eerst een PDF bestand');
      return;
    }

    setElektriciteitUploading(true);
    try {
      const response = await offerteAPI.createElektriciteit(id, elektriciteitFile);
      
      if (response.data.success) {
        toast.success('PDF geüpload en klantgegevens verzonden!');
        setElektriciteitDialogOpen(false);
        setElektriciteitFile(null);
        
        // Open the Google Sheet for Elektriciteit offertes
        window.open('https://docs.google.com/spreadsheets/d/1Jp2j7rcPWuBbOTLCKtTaYlxvgJ-P0I-4Ww-NOxoLkW0/edit?gid=609459326#gid=609459326', '_blank');
      }
    } catch (error) {
      console.error('Elektriciteit offerte error:', error);
      toast.error(error.response?.data?.detail || 'Kon offerte niet verwerken');
    } finally {
      setElektriciteitUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-32 animate-pulse bg-muted rounded-lg" />
        <div className="h-64 animate-pulse bg-muted rounded-lg" />
      </div>
    );
  }

  if (!data) return null;

  const { property, customer, installations, maintenances, cleanings, documents } = data;

  // Calculate last cleaning info
  const lastCleaning = cleanings.length > 0 
    ? cleanings.reduce((a, b) => new Date(a.cleaning_date) > new Date(b.cleaning_date) ? a : b)
    : null;
  
  // Calculate next cleaning due date if there's a contract
  const getNextCleaningDue = () => {
    if (!property.cleaning_contract || !lastCleaning) return null;
    
    const lastDate = new Date(lastCleaning.cleaning_date);
    const years = property.cleaning_contract === '2_yearly' ? 2 : 3;
    const nextDue = new Date(lastDate);
    nextDue.setFullYear(nextDue.getFullYear() + years);
    
    return {
      date: nextDue,
      isOverdue: nextDue < new Date(),
      isUpcoming: nextDue < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // within 30 days
    };
  };
  
  const nextCleaningDue = getNextCleaningDue();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="property-detail-page">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate('/properties')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Properties
      </Button>

      {/* Property Header */}
      <Card className="border-border/50">
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col gap-4">
            {/* Address Info */}
            <div className="flex items-center gap-3 md:gap-4">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 md:w-7 md:h-7 text-blue-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h1 className="text-lg md:text-2xl font-bold tracking-tight">
                    {property.street} {property.number}
                  </h1>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-primary hover:text-primary/80" title="Navigatie">
                        <MapPin className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuLabel className="text-xs text-muted-foreground">Navigeren naar</DropdownMenuLabel>
                      <DropdownMenuItem asChild>
                        <a 
                          href={getGoogleMapsUrl(property)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center cursor-pointer"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Google Maps
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <a 
                          href={getWazeUrl(property)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center cursor-pointer"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Waze
                        </a>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <p className="text-sm text-muted-foreground">
                  {property.postal_code} {property.city}, {property.country}
                </p>
                {customer && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Owner: <span 
                      className="text-primary cursor-pointer hover:underline"
                      onClick={() => navigate(`/customers/${customer.id}`)}
                    >
                      {customer.first_name} {customer.last_name}
                    </span>
                  </p>
                )}
              </div>
            </div>

            {/* Edit Address Button - under address */}
            {hasRole(['admin', 'sales']) && (
              <Button 
                onClick={handleEditProperty}
                variant="outline"
                size="sm"
                className="w-fit"
              >
                <Edit className="w-3 h-3 mr-1" />
                Adres Bewerken
              </Button>
            )}

          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        {hasRole(['admin', 'technician']) && (
          <>
            <Button size="sm" onClick={() => setInstallationDialog(true)} data-testid="add-installation-btn">
              <Plus className="w-4 h-4 mr-2" />
              Installatie Toevoegen
            </Button>
            <Button variant="outline" size="sm" onClick={() => setMaintenanceDialog(true)}>
              <Wrench className="w-4 h-4 mr-2" />
              Onderhoud Toevoegen
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCleaningDialog(true)}>
              <Sparkles className="w-4 h-4 mr-2" />
              Reiniging Inplannen
            </Button>
          </>
        )}
        <Button variant="outline" size="sm" onClick={() => setUploadDialog(true)}>
          <Upload className="w-4 h-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Status Tracking Dropdown */}
      <div className="flex flex-wrap items-center gap-3">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="status-tracking-dropdown">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              Status & Contact
              {/* Show count of checked items */}
              {(() => {
                const checkedCount = ['call_1', 'call_2', 'call_3', 'mail_1', 'mail_2', 'mail_3', 'interesse_reiniging', 'offerte_ontvangen']
                  .filter(field => property?.[field]).length;
                return checkedCount > 0 ? (
                  <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">{checkedCount}</Badge>
                ) : null;
              })()}
              <ChevronDown className="w-4 h-4 ml-1" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-4" align="start">
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center gap-2 pb-2 border-b">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <span className="font-medium">Status & Contact Tracking</span>
              </div>
              
              {/* Contact - Calls */}
              <div className="space-y-2">
                <h4 className="text-xs font-medium flex items-center gap-2 text-muted-foreground uppercase tracking-wide">
                  <Phone className="w-3 h-3" />
                  Telefonisch
                </h4>
                <div className="space-y-1.5">
                  {[
                    { field: 'call_1', label: '1ste call' },
                    { field: 'call_2', label: '2de call' },
                    { field: 'call_3', label: '3de call' },
                  ].map(({ field, label }) => (
                    <div key={field} className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${field}`}
                        checked={property?.[field] || false}
                        onCheckedChange={(checked) => handleStatusChange(field, checked)}
                        data-testid={`status-${field}`}
                      />
                      <label htmlFor={`status-${field}`} className="text-sm cursor-pointer flex-1">
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact - Mails */}
              <div className="space-y-2">
                <h4 className="text-xs font-medium flex items-center gap-2 text-muted-foreground uppercase tracking-wide">
                  <Mail className="w-3 h-3" />
                  Email
                </h4>
                <div className="space-y-1.5">
                  {[
                    { field: 'mail_1', label: '1ste mail' },
                    { field: 'mail_2', label: '2de mail' },
                    { field: 'mail_3', label: '3de mail' },
                  ].map(({ field, label }) => (
                    <div key={field} className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${field}`}
                        checked={property?.[field] || false}
                        onCheckedChange={(checked) => handleStatusChange(field, checked)}
                        data-testid={`status-${field}`}
                      />
                      <label htmlFor={`status-${field}`} className="text-sm cursor-pointer flex-1">
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sales Pipeline */}
              <div className="space-y-2">
                <h4 className="text-xs font-medium flex items-center gap-2 text-muted-foreground uppercase tracking-wide">
                  <Sparkles className="w-3 h-3" />
                  Reiniging Pipeline
                </h4>
                <div className="space-y-1.5">
                  {[
                    { field: 'interesse_reiniging', label: 'Interesse reiniging' },
                    { field: 'offerte_ontvangen', label: 'Offerte ontvangen' },
                  ].map(({ field, label }) => (
                    <div key={field} className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${field}`}
                        checked={property?.[field] || false}
                        onCheckedChange={(checked) => handleStatusChange(field, checked)}
                        data-testid={`status-${field}`}
                      />
                      <label 
                        htmlFor={`status-${field}`} 
                        className="text-sm cursor-pointer flex-1"
                      >
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Reiniging Status badges - always visible */}
        {property.cleaning_contract && (
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <Badge variant="outline" className={property.cleaning_contract === '2_yearly' ? 'border-amber-500 text-amber-500' : 'border-blue-500 text-blue-500'}>
              {property.cleaning_contract === '2_yearly' ? '2-jaarlijks' : '3-jaarlijks'}
            </Badge>
          </div>
        )}
        
        {lastCleaning && (
          <div className="flex items-center gap-2 text-sm">
            <CalendarIcon className="w-4 h-4 text-green-400" />
            <span className="text-green-400">{formatDate(lastCleaning.cleaning_date)}</span>
          </div>
        )}
        
        {nextCleaningDue && (
          <div className="flex items-center gap-2 text-sm">
            <AlertTriangle className={`w-4 h-4 ${nextCleaningDue.isOverdue ? 'text-red-400' : nextCleaningDue.isUpcoming ? 'text-amber-400' : 'text-muted-foreground'}`} />
            <span className={nextCleaningDue.isOverdue ? 'text-red-400 font-medium' : nextCleaningDue.isUpcoming ? 'text-amber-400' : ''}>
              {nextCleaningDue.date.toLocaleDateString('nl-BE')}
              {nextCleaningDue.isOverdue && ' (VERLOPEN)'}
            </span>
          </div>
        )}
        
        {property.cleaning_contract && !lastCleaning && (
          <Badge variant="outline" className="border-amber-500 text-amber-500">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Nog geen reiniging
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue={searchParams.get('tab') || 'installations'} className="w-full">
        <TabsList>
          <TabsTrigger value="installations">Installations ({installations.length})</TabsTrigger>
          <TabsTrigger value="maintenance">Maintenance ({maintenances.length})</TabsTrigger>
          <TabsTrigger value="cleaning">Cleaning ({cleanings.length})</TabsTrigger>
          <TabsTrigger value="documents">Documents ({documents.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="installations" className="mt-6">
          {installations.length === 0 ? (
            <Card className="p-8 text-center">
              <Sun className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No installations yet</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {installations.map((inst) => (
                <Card key={inst.id} className="border-border/50">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${
                          inst.installation_type === 'solar_panels' ? 'bg-amber-500/20' :
                          inst.installation_type === 'battery' ? 'bg-blue-500/20' :
                          inst.installation_type === 'ev_charger' ? 'bg-green-500/20' : 'bg-purple-500/20'
                        }`}>
                          {inst.installation_type === 'solar_panels' && <Sun className="w-5 h-5 text-amber-400" />}
                          {inst.installation_type === 'battery' && <Battery className="w-5 h-5 text-blue-400" />}
                          {inst.installation_type === 'ev_charger' && <Zap className="w-5 h-5 text-green-400" />}
                          {inst.installation_type === 'inverter' && <Sun className="w-5 h-5 text-purple-400" />}
                        </div>
                        <div>
                          <p className="font-medium">{getInstallationTypeLabel(inst.installation_type)}</p>
                          {inst.installation_date && (
                            <p className="text-xs text-muted-foreground">Geïnstalleerd: {formatDate(inst.installation_date)}</p>
                          )}
                        </div>
                      </div>
                      {hasRole(['admin', 'technician']) && (
                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-muted-foreground hover:text-primary"
                            onClick={() => handleEditInstallation(inst)}
                            title="Installatie bewerken"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDeleteInstallation(inst.id)}
                            title="Installatie verwijderen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-1 text-sm">
                      {inst.installation_type === 'solar_panels' && (
                        <>
                          {inst.number_of_panels && <p><span className="text-muted-foreground">Panelen:</span> {inst.number_of_panels} stuks</p>}
                          {inst.panel_brand && <p><span className="text-muted-foreground">Merk:</span> {inst.panel_brand} {inst.panel_model}</p>}
                          {inst.panel_wattage && <p><span className="text-muted-foreground">Vermogen:</span> {inst.panel_wattage} Wp</p>}
                          {(inst.inverter_brand || inst.inverter_model) && (
                            <p><span className="text-muted-foreground">Omvormer:</span> {inst.inverter_brand} {inst.inverter_model}</p>
                          )}
                        </>
                      )}
                      {inst.installation_type === 'inverter' && (
                        <>
                          {inst.inverter_brand && <p><span className="text-muted-foreground">Merk:</span> {inst.inverter_brand}</p>}
                          {inst.inverter_model && <p><span className="text-muted-foreground">Model:</span> {inst.inverter_model}</p>}
                        </>
                      )}
                      {inst.installation_type === 'battery' && (
                        <>
                          {inst.battery_brand && <p><span className="text-muted-foreground">Merk:</span> {inst.battery_brand}</p>}
                          {inst.battery_capacity && <p><span className="text-muted-foreground">Capaciteit:</span> {inst.battery_capacity}</p>}
                        </>
                      )}
                      {inst.installation_type === 'ev_charger' && (
                        <>
                          {inst.charger_brand && <p><span className="text-muted-foreground">Merk:</span> {inst.charger_brand}</p>}
                          {inst.charger_power && <p><span className="text-muted-foreground">Vermogen:</span> {inst.charger_power}</p>}
                        </>
                      )}
                      {inst.warranty_until && <p><span className="text-muted-foreground">Garantie tot:</span> {formatDate(inst.warranty_until)}</p>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="maintenance" className="mt-6">
          {maintenances.length === 0 ? (
            <Card className="p-8 text-center">
              <Wrench className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No maintenance records</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {maintenances.map((m) => (
                <Card key={m.id} className="border-border/50">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-md bg-secondary flex items-center justify-center">
                        <Wrench className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium">{m.maintenance_type}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            {formatDate(m.maintenance_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {m.technician}
                          </span>
                        </div>
                      </div>
                    </div>
                    {m.notes && <p className="text-sm text-muted-foreground max-w-xs truncate">{m.notes}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="cleaning" className="mt-6">
          {cleanings.length === 0 ? (
            <Card className="p-8 text-center">
              <Sparkles className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No cleaning records</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {cleanings.map((c) => (
                <Card key={c.id} className="border-border/50">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-md bg-purple-500/20 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="font-medium">Panel Cleaning</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            {formatDate(c.cleaning_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {c.performed_by}
                          </span>
                        </div>
                      </div>
                    </div>
                    {c.notes && <p className="text-sm text-muted-foreground max-w-xs truncate">{c.notes}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          {hasRole('admin') && documents.some(d => !String(d.id).startsWith('sp-')) && (
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <p className="text-sm text-muted-foreground">
                {selectedDocIds.length > 0
                  ? `${selectedDocIds.length} geselecteerd`
                  : 'Vink foto\'s of documenten aan om ze naar de Sharepoint klantmap te pushen'}
              </p>
              <Button
                size="sm"
                disabled={selectedDocIds.length === 0 || bulkPushing}
                onClick={handleBulkPushToSharepoint}
                data-testid="bulk-push-sharepoint-btn"
              >
                {bulkPushing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Push selectie naar klantmap{selectedDocIds.length > 0 ? ` (${selectedDocIds.length})` : ''}
              </Button>
            </div>
          )}
          {documents.length === 0 ? (
            <Card className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Geen documenten geüpload</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map((doc) => (
                <Card key={doc.id} className={`border-border/50 hover:border-primary/30 transition-colors ${selectedDocIds.includes(doc.id) ? 'border-primary/60 bg-primary/5' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {hasRole('admin') && !String(doc.id).startsWith('sp-') && (
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 accent-primary cursor-pointer flex-shrink-0"
                          checked={selectedDocIds.includes(doc.id)}
                          onChange={() => toggleDocSelection(doc.id)}
                          data-testid={`select-doc-${doc.id}`}
                        />
                      )}
                      {/* Show thumbnail for images */}
                      {(doc.document_type === 'photo' || doc.document_type === 'cleaning_photo' || doc.document_type === 'installation_photo' || 
                        ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(doc.filename?.split('.').pop()?.toLowerCase())) ? (
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                          <img 
                            src={getPreviewUrl(doc)} 
                            alt={doc.filename}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="w-full h-full bg-primary/10 items-center justify-center hidden">
                            <FileText className="w-6 h-6 text-primary" />
                          </div>
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate" title={doc.original_filename || doc.filename}>
                          {doc.original_filename || doc.filename}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-xs capitalize">
                            {doc.document_type === 'invoice' ? 'Factuur' : 
                             doc.document_type === 'quote' ? 'Offerte' :
                             doc.document_type === 'contract' ? 'Contract' :
                             doc.document_type === 'report' ? 'Rapport' :
                             doc.document_type === 'certificate' ? 'Certificaat' :
                             doc.document_type === 'photo' ? 'Foto' :
                             doc.document_type === 'cleaning_photo' ? 'Reiniging Foto' :
                             doc.document_type === 'installation_photo' ? 'Installatie Foto' : 'Overig'}
                          </Badge>
                          {doc.google_drive_file_id && (
                            <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-500 border-blue-500/30">
                              <HardDrive className="w-3 h-3 mr-1" />
                              Drive
                            </Badge>
                          )}
                          {doc.file_size && (
                            <span className="text-xs text-muted-foreground">
                              {(doc.file_size / 1024).toFixed(1)} KB
                            </span>
                          )}
                        </div>
                        {doc.description && (
                          <p className="text-xs text-muted-foreground mt-1 truncate" title={doc.description}>
                            {doc.description}
                          </p>
                        )}
                        {doc.uploaded_at && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(doc.uploaded_at)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50">
                      {/* Preview button - opens in-app preview modal */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={() => handlePreviewDocument(doc)}
                        data-testid={`preview-doc-${doc.id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Bekijken
                      </Button>
                      {/* Download button */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        asChild
                      >
                        <a 
                          href={doc.google_drive_download_url || `${process.env.REACT_APP_BACKEND_URL}${doc.local_path || doc.file_path?.replace('/uploads/', '/api/uploads/')}`}
                          download={doc.original_filename || doc.filename}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Download className="w-3 h-3" />
                        </a>
                      </Button>
                      {/* Push naar Sharepoint klantmap (niet voor bestanden die er al vandaan komen) */}
                      {hasRole('admin') && !String(doc.id).startsWith('sp-') && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          title="Naar klantmap pushen"
                          disabled={pushingDocId === doc.id}
                          onClick={() => handlePushToSharepoint(doc)}
                          data-testid={`push-sharepoint-${doc.id}`}
                        >
                          {pushingDocId === doc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        </Button>
                      )}
                      {hasRole('admin') && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs text-destructive hover:text-destructive"
                          onClick={() => {
                            setDocumentToDelete(doc);
                            setDeleteDocumentDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Installation Dialog */}
      <Dialog open={installationDialog} onOpenChange={setInstallationDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Installatie Toevoegen</DialogTitle>
            <DialogDescription>Voeg een nieuwe installatie toe aan dit adres</DialogDescription>
          </DialogHeader>
          <InstallationForm
            propertyId={id}
            onSubmit={handleCreateInstallation}
            onCancel={() => setInstallationDialog(false)}
            loading={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Maintenance Dialog */}
      <Dialog open={maintenanceDialog} onOpenChange={setMaintenanceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Onderhoud Toevoegen</DialogTitle>
            <DialogDescription>Registreer een onderhoudsactiviteit</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateMaintenance} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Datum *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {maintenanceForm.maintenance_date ? format(parse(maintenanceForm.maintenance_date, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: nl }) : 'Selecteer datum'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={maintenanceForm.maintenance_date ? parse(maintenanceForm.maintenance_date, 'yyyy-MM-dd', new Date()) : undefined}
                      onSelect={(date) => date && setMaintenanceForm(prev => ({ ...prev, maintenance_date: format(date, 'yyyy-MM-dd') }))}
                      locale={nl}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Technicus *</Label>
                <Input
                  value={maintenanceForm.technician}
                  onChange={(e) => setMaintenanceForm(prev => ({ ...prev, technician: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Type *</Label>
              <Input
                value={maintenanceForm.maintenance_type}
                onChange={(e) => setMaintenanceForm(prev => ({ ...prev, maintenance_type: e.target.value }))}
                placeholder="bijv. Jaarlijkse inspectie, Omvormer reparatie"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Notities</Label>
              <Textarea
                value={maintenanceForm.notes}
                onChange={(e) => setMaintenanceForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMaintenanceDialog(false)}>Annuleren</Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Opslaan...' : 'Toevoegen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cleaning Dialog */}
      <Dialog open={cleaningDialog} onOpenChange={setCleaningDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reiniging Inplannen</DialogTitle>
            <DialogDescription>Plan een reinigingsactiviteit</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCleaning} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {cleaningForm.cleaning_date ? format(parse(cleaningForm.cleaning_date, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: nl }) : 'Selecteer datum'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={cleaningForm.cleaning_date ? parse(cleaningForm.cleaning_date, 'yyyy-MM-dd', new Date()) : undefined}
                      onSelect={(date) => date && setCleaningForm(prev => ({ ...prev, cleaning_date: format(date, 'yyyy-MM-dd') }))}
                      locale={nl}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Performed By *</Label>
                <Input
                  value={cleaningForm.performed_by}
                  onChange={(e) => setCleaningForm(prev => ({ ...prev, performed_by: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={cleaningForm.notes}
                onChange={(e) => setCleaningForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCleaningDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Add Record'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Document Uploaden</DialogTitle>
            <DialogDescription>Upload een document voor dit adres</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUploadDocument} className="space-y-4">
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select 
                value={uploadForm.document_type} 
                onValueChange={(value) => setUploadForm(prev => ({ ...prev, document_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice">Factuur</SelectItem>
                  <SelectItem value="quote">Offerte</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="report">Rapport</SelectItem>
                  <SelectItem value="certificate">Certificaat</SelectItem>
                  <SelectItem value="photo">Foto</SelectItem>
                  <SelectItem value="other">Overig</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Bestanden * (meerdere mogelijk)</Label>
              <Input
                type="file"
                multiple
                onChange={(e) => setUploadForm(prev => ({ ...prev, files: Array.from(e.target.files || []) }))}
                required={uploadForm.files.length === 0}
                data-testid="upload-multi-file-input"
              />
              {uploadForm.files && uploadForm.files.length > 0 && (
                <div className="space-y-1 mt-2 max-h-40 overflow-y-auto">
                  <p className="text-xs font-medium text-muted-foreground">
                    {uploadForm.files.length} bestand{uploadForm.files.length > 1 ? 'en' : ''} geselecteerd
                  </p>
                  {uploadForm.files.map((f, i) => (
                    <div key={`${f.name}-${i}`} className="flex items-center justify-between text-xs bg-muted/40 px-2 py-1 rounded">
                      <span className="truncate flex-1">
                        {f.name} <span className="text-muted-foreground">({(f.size / 1024).toFixed(1)} KB)</span>
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={() => setUploadForm(prev => ({
                          ...prev,
                          files: prev.files.filter((_, idx) => idx !== i),
                        }))}
                        title="Verwijder uit selectie"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Beschrijving (optioneel)</Label>
              <Input
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Bijv. Factuur mei 2024"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUploadDialog(false)}>Annuleren</Button>
              <Button type="submit" disabled={saving || uploadForm.files.length === 0}>
                {saving ? `Uploaden (${uploadForm.files.length})...` : `Upload ${uploadForm.files.length > 0 ? `${uploadForm.files.length} bestand${uploadForm.files.length > 1 ? 'en' : ''}` : ''}`.trim() || 'Uploaden'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Document Dialog */}
      <AlertDialog open={deleteDocumentDialogOpen} onOpenChange={setDeleteDocumentDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Document verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je "{documentToDelete?.original_filename || documentToDelete?.filename}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDocumentToDelete(null)}>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDocument} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Installatie Type Selection Modal */}
      <Dialog open={installatieModalOpen} onOpenChange={setInstallatieModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {installatieModalType === 'offerte' ? 'Offerte installatie' : 'Factuur installatie'}
            </DialogTitle>
            <DialogDescription>
              Selecteer het type installatie
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button 
              variant="outline" 
              className="w-full h-12 text-base justify-start px-4 hover:bg-blue-500/10 hover:border-blue-500/50"
              onClick={() => handleInstallatieTypeSelect('SAJ')}
              data-testid="installatie-type-saj"
            >
              <Sun className="w-5 h-5 mr-3 text-blue-400" />
              SAJ
            </Button>
            <Button 
              variant="outline" 
              className="w-full h-12 text-base justify-start px-4 hover:bg-amber-500/10 hover:border-amber-500/50"
              onClick={() => handleInstallatieTypeSelect('Solarwatt')}
              data-testid="installatie-type-solarwatt"
            >
              <Sun className="w-5 h-5 mr-3 text-amber-400" />
              Solarwatt
            </Button>
            <Button 
              variant="outline" 
              className="w-full h-12 text-base justify-start px-4 hover:bg-green-500/10 hover:border-green-500/50"
              onClick={() => handleInstallatieTypeSelect('DMEGC')}
              data-testid="installatie-type-dmegc"
            >
              <Sun className="w-5 h-5 mr-3 text-green-400" />
              DMEGC
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Offerte Reiniging Modal - Simplified */}
      <Dialog open={offerteReinigingModalOpen} onOpenChange={setOfferteReinigingModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Offerte reiniging aanmaken</DialogTitle>
            <DialogDescription>
              {property && (
                <>Offerte voor {property.street} {property.number}, {property.city}</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Er wordt een offerte aangemaakt voor de reiniging van de PV-installatie op dit adres. 
              Na het aanmaken kun je kiezen om een bevestigingsmail te versturen.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOfferteReinigingModalOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleCreateOfferteInCRM}>
              <FileText className="w-4 h-4 mr-2" />
              Offerte aanmaken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Confirmation Modal - After offerte creation */}
      <Dialog open={emailConfirmModalOpen} onOpenChange={setEmailConfirmModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Offerte aangemaakt!</DialogTitle>
            <DialogDescription>
              De offerte is succesvol aangemaakt. Wil je een bevestigingsmail versturen naar de klant?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {customer && (
              <p className="text-sm">
                <Mail className="w-4 h-4 inline mr-2" />
                Mail wordt verzonden naar: <span className="font-medium">{customer.email || 'Geen email bekend'}</span>
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleSkipEmail}>
              Nee, later
            </Button>
            <Button onClick={handleSendOfferteEmail} disabled={!customer?.email}>
              <Mail className="w-4 h-4 mr-2" />
              Ja, mail versturen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Property (Address) Dialog */}
      <Dialog open={editPropertyDialogOpen} onOpenChange={setEditPropertyDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adres Bewerken</DialogTitle>
            <DialogDescription>Pas de adresgegevens aan</DialogDescription>
          </DialogHeader>
          {editPropertyForm && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Straat</Label>
                  <Input
                    value={editPropertyForm.street}
                    onChange={(e) => setEditPropertyForm(prev => ({ ...prev, street: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nummer</Label>
                  <Input
                    value={editPropertyForm.number}
                    onChange={(e) => setEditPropertyForm(prev => ({ ...prev, number: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Postcode</Label>
                  <Input
                    value={editPropertyForm.postal_code}
                    onChange={(e) => setEditPropertyForm(prev => ({ ...prev, postal_code: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Gemeente</Label>
                  <Input
                    value={editPropertyForm.city}
                    onChange={(e) => setEditPropertyForm(prev => ({ ...prev, city: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Land</Label>
                <Input
                  value={editPropertyForm.country}
                  onChange={(e) => setEditPropertyForm(prev => ({ ...prev, country: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Notities</Label>
                <Textarea
                  value={editPropertyForm.notes}
                  onChange={(e) => setEditPropertyForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditPropertyDialogOpen(false)}>
                  Annuleren
                </Button>
                <Button onClick={handleSaveProperty} disabled={savingProperty}>
                  {savingProperty ? 'Opslaan...' : 'Bijwerken'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Installation Dialog */}
      <Dialog open={editInstallationDialogOpen} onOpenChange={(open) => {
        setEditInstallationDialogOpen(open);
        if (!open) setEditInstallation(null);
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Installatie Bewerken</DialogTitle>
            <DialogDescription>Pas de installatiegegevens aan</DialogDescription>
          </DialogHeader>
          <InstallationForm
            propertyId={id}
            installation={editInstallation}
            onSubmit={handleSaveInstallation}
            onCancel={() => {
              setEditInstallationDialogOpen(false);
              setEditInstallation(null);
            }}
            loading={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Installation Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Installatie verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze installatie wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setInstallationToDelete(null)}>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteInstallation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-installation-btn"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Offerte Elektriciteit Dialog */}
      <Dialog open={elektriciteitDialogOpen} onOpenChange={setElektriciteitDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Offerte Elektriciteit</DialogTitle>
            <DialogDescription>
              Upload een PDF bestand. Dit wordt automatisch naar Google Drive geüpload en de klantgegevens worden naar n8n verzonden.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Customer Info */}
            {customer && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <p className="font-medium">{customer.first_name} {customer.last_name}</p>
                {customer.email && <p className="text-muted-foreground">{customer.email}</p>}
                {property && (
                  <p className="text-muted-foreground">{property.street} {property.number}, {property.city}</p>
                )}
              </div>
            )}

            {/* File Upload */}
            <div className="space-y-2">
              <Label htmlFor="elektriciteit-pdf">PDF Bestand *</Label>
              <div className="flex flex-col gap-2">
                <Input
                  id="elektriciteit-pdf"
                  ref={elektriciteitInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleElektriciteitFileChange}
                  className="cursor-pointer"
                  data-testid="elektriciteit-file-input"
                />
                {elektriciteitFile && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground bg-green-500/10 border border-green-500/30 rounded-md p-2">
                    <FileText className="w-4 h-4 text-green-500" />
                    <span className="truncate flex-1">{elektriciteitFile.name}</span>
                    <span>({(elektriciteitFile.size / 1024).toFixed(1)} KB)</span>
                  </div>
                )}
              </div>
            </div>

            {/* Info text */}
            <div className="text-xs text-muted-foreground bg-blue-500/10 border border-blue-500/30 rounded-md p-3">
              <p className="font-medium text-blue-400 mb-1">Wat gebeurt er?</p>
              <ul className="list-disc list-inside space-y-1">
                <li>PDF wordt geüpload naar Google Drive (Elektriciteit map)</li>
                <li>Klantgegevens worden automatisch naar n8n verzonden</li>
                <li>n8n detecteert de nieuwe PDF in Drive en verwerkt deze</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setElektriciteitDialogOpen(false);
                setElektriciteitFile(null);
              }}
              disabled={elektriciteitUploading}
            >
              Annuleren
            </Button>
            <Button 
              onClick={handleElektriciteitSubmit}
              disabled={!elektriciteitFile || elektriciteitUploading}
              data-testid="elektriciteit-submit-btn"
            >
              {elektriciteitUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploaden...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload & Verzenden
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-5xl h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {previewDocument?.original_filename || previewDocument?.filename || 'Document'}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-3">
              <Badge variant="outline" className="text-xs capitalize">
                {previewDocument?.document_type === 'invoice' ? 'Factuur' : 
                 previewDocument?.document_type === 'quote' ? 'Offerte' :
                 previewDocument?.document_type === 'contract' ? 'Contract' :
                 previewDocument?.document_type === 'report' ? 'Rapport' :
                 previewDocument?.document_type === 'certificate' ? 'Certificaat' :
                 previewDocument?.document_type === 'photo' ? 'Foto' : 'Overig'}
              </Badge>
              {previewDocument?.uploaded_at && (
                <span className="text-xs">Geüpload: {formatDate(previewDocument.uploaded_at)}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden bg-muted/30">
            {previewDocument && (
              <>
                {getFileType(previewDocument.filename) === 'pdf' ? (
                  <div className="w-full h-full flex flex-col">
                    {/* Try to show PDF inline, with fallback */}
                    <iframe
                      src={`https://docs.google.com/viewer?url=${encodeURIComponent(getPreviewUrl(previewDocument))}&embedded=true`}
                      className="w-full flex-1 border-0"
                      title="PDF Preview"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                ) : getFileType(previewDocument.filename) === 'image' ? (
                  <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
                    <img 
                      src={getPreviewUrl(previewDocument)} 
                      alt={previewDocument.filename}
                      className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                    />
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center">
                    <FileText className="w-16 h-16 text-muted-foreground mb-4" />
                    <p className="text-lg font-medium mb-2">Preview niet beschikbaar</p>
                    <p className="text-muted-foreground mb-4">
                      Dit bestandstype kan niet direct worden weergegeven.
                    </p>
                    <Button asChild>
                      <a 
                        href={previewDocument.google_drive_download_url || `${process.env.REACT_APP_BACKEND_URL}${previewDocument.file_path}`}
                        download={previewDocument.original_filename || previewDocument.filename}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download bestand
                      </a>
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="px-6 py-3 border-t flex items-center justify-between bg-background">
            <div className="flex items-center gap-2">
              {previewDocument?.google_drive_url && (
                <Button variant="outline" size="sm" asChild>
                  <a href={previewDocument.google_drive_url} target="_blank" rel="noopener noreferrer">
                    <HardDrive className="w-4 h-4 mr-2" />
                    Open in Drive
                  </a>
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <a 
                  href={getDownloadUrl(previewDocument)}
                  download={previewDocument?.original_filename || previewDocument?.filename}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </a>
              </Button>
              <Button variant="default" size="sm" onClick={() => setPreviewDialogOpen(false)}>
                Sluiten
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyDetail;
